"use client";

import React, { useState, useCallback, useEffect } from "react";
import { useReactiveSearchParams } from "@/lib/Hooks/useReactiveSearchParams";
import styled from "styled-components";
import { Checkbox, Text } from "@lmn/design-system";
import initialState from "@/lib/StaticData/searchFormData";
import Accordion from "@/components/UI/Accordion/Accordion";
import IconActivities from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconActivities.js";
import IconFamily from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconFamily.js";
import IconSpa from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconSpa.js";
import { useFilterState } from "@/lib/Providers/FilterStateProvider/FilterStateProvider";

const FilterWrapper = styled.div`
  width: 100%;
  margin-top: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FilterContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  position: relative;

  & label {
    justify-content: flex-start;
  }
`;

const AccordionContent = styled.div`
  margin-top: 0.5rem;
`;

const Separator = styled.div`
  height: 1px;
  width: 100%;
  background-color: ${({ theme }) =>
    theme.kallisto.color.background.static.backgroundDisabled};
`;

const AccordionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
`;

const ClickOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  cursor: pointer;
  z-index: 1;
`;

interface HolidayTypesFilterProps {
  delayedUpdate?: boolean;
}

const HolidayTypesFilter: React.FC<HolidayTypesFilterProps> = ({
  delayedUpdate = false,
}) => {
  const searchParams = useReactiveSearchParams();
  const { tempFilters, updateFilter, updateTempFilterArray } = useFilterState();
  const topFamily = initialState.topFamily;
  const topWellness = initialState.topWellness;
  const topSport = initialState.topSport;

  const [selectedCategories, setSelectedCategories] = useState<number[]>(() => {
    return delayedUpdate
      ? tempFilters.get("categories")?.split(",").map(Number) ?? []
      : searchParams.get("categories")?.split(",").map(Number) ?? [];
  });

  useEffect(() => {
    setSelectedCategories(
      searchParams.get("categories")?.split(",").map(Number) ?? []
    );
  }, [searchParams]);

  const handleCheckboxChange = useCallback(
    (id: number) => {
      const isCurrentlyChecked = selectedCategories.includes(id);
      const newCategories = isCurrentlyChecked
        ? selectedCategories.filter((catId) => catId !== id)
        : [...selectedCategories, id];

      setSelectedCategories(newCategories);

      if (delayedUpdate) {
        // Just update temp filters, will be committed on dialog confirmation
        updateTempFilterArray("categories", newCategories.map(String));
      } else {
        // Immediate URL update
        const categoryStrings = newCategories.map(String);
        if (categoryStrings.length > 0) {
          updateFilter("categories", categoryStrings.join(","));
        } else {
          updateFilter("categories", null);
        }
      }
    },
    [selectedCategories, updateFilter, delayedUpdate, updateTempFilterArray]
  );

  const renderCategorySection = (
    title: string,
    icon: React.ReactNode,
    categories: { allIds: number[]; byId: Record<number, { label: string }> }
  ) => (
    <>
      <Accordion
        trigger={
          <AccordionTitle>
            {icon}
            <Text forceTag="h4" margin={0}>
              {title}
            </Text>
          </AccordionTitle>
        }
        triggerPosition="top"
        withChevronRight={true}
        isOpenByDefault={false}
      >
        <AccordionContent>
          {categories.allIds.map((id) => {
            const criteria = categories.byId[id];
            const isChecked = selectedCategories.includes(id);

            return (
              <FilterContent key={id}>
                {delayedUpdate && (
                  <ClickOverlay onClick={() => handleCheckboxChange(id)} />
                )}
                <Checkbox
                  checkbox={{
                    id: `holiday-${id}`,
                    label: criteria.label,
                  }}
                  isChecked={isChecked}
                  onChange={
                    delayedUpdate ? () => { } : () => handleCheckboxChange(id)
                  }
                />
              </FilterContent>
            );
          })}
        </AccordionContent>
      </Accordion>
      <Separator />
    </>
  );

  return (
    <FilterWrapper data-testid="holidayTypesFilter">
      {renderCategorySection("Familie", <IconFamily size={24} />, topFamily)}
      {renderCategorySection("Wellness", <IconSpa size={24} />, topWellness)}
      {renderCategorySection(
        "Sportangebot",
        <IconActivities size={24} />,
        topSport
      )}
    </FilterWrapper>
  );
};

export default HolidayTypesFilter;
